import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import type { Notification } from '@/types/notifications';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsSheet({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { list, markRead, markAllRead, dismiss } = useNotifications({ enabled: true });

  // Poll-on-open: fetch the list + mark-all-read when the Sheet opens.
  useEffect(() => {
    if (open) {
      list.refetch();
      markAllRead();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (n: Notification) => {
    markRead(n.id);
    onOpenChange(false);
    // Only follow app-relative paths (defense against an open-redirect if a
    // future source ever lets a value into deep_link).
    if (n.deep_link && n.deep_link.startsWith('/') && !n.deep_link.startsWith('//')) {
      navigate(n.deep_link);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader className="flex-row items-center justify-between">
          <SheetTitle>Notifications</SheetTitle>
          <Button variant="ghost" size="sm" onClick={() => markAllRead()}>
            Mark all read
          </Button>
        </SheetHeader>

        <ScrollArea className="-mx-2 flex-1 px-2">
          {list.isFetching && !list.data ? (
            <div className="space-y-2 py-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : list.isError ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
              <span>Couldn’t load notifications.</span>
              <Button variant="outline" size="sm" onClick={() => list.refetch()}>
                Try again
              </Button>
            </div>
          ) : !list.data?.length ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              You’re all caught up.
            </div>
          ) : (
            <div className="space-y-2 py-2">
              {list.data.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onClick={handleClick}
                  onDismiss={dismiss}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
